import type { Dictionary } from '@sembo-private/asq';
import { asq, filter } from '@sembo-private/asq';
import { ConnectionType } from '@sembo-private/maps';
import { computed, ref } from 'vue';
import { last } from 'lodash';

import { assertValue, exhaustiveGuard, isDefined } from '@libs/utils';
import type {
  ActivityTicket,
  AutoCompleteSuggestionsByCategory,
  IngredientId,
  Pax,
  RecipeMetaData,
  TransportDirection,
} from '@libs/trip-planner-interfaces';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';
import type { MonthIndex } from '@libs/sembo-dates';
import { SemboDate } from '@libs/sembo-dates';
import { trackDestinationRemoved } from '@libs/google-analytics-events';

import {
  DEFAULT_TRANSPORT_TYPE,
  DEFAULT_TRANSPORT_TYPE_BETWEEN_DESTINATION,
  isFerryTransport,
  isFlightTransport,
} from '../helpers/transports';
import type {
  RecipeMainIngredient,
  RecipeIngredient,
  ActivityIngredient,
} from '../recipe/ingredients';
import {
  HotelIngredient,
  FlightIngredient,
  isHotelIngredient,
  isFlightIngredient,
} from '../recipe/ingredients';
import { TripPlannerPax } from '../recipe/pax';
import {
  isFerryIngredient,
  isOwnStayIngredient,
  isStayIngredient,
  isTransportIngredient,
} from '../recipe/ingredients/typeGuards';
import { FerryIngredient } from '../recipe/ingredients/ferryIngredient';
import type { TransportIngredient } from '../recipe/ingredients/transportIngredient';
import { FlightEndpoint } from '../api/flight';
import { usePreferredTimeStore } from '../store/preferredTimeStore';
import { useEditingIngredientStore } from '../store/editingIngredientStore';
import type { UpdateIngredientByIdData } from '../recipe/ingredients/updateIngredient';
import { UpdateIngredientDataType } from '../recipe/ingredients/updateIngredient';
import type { OwnStayIngredient } from '../recipe/ingredients/ownStayIngredient';

export const DEFAULT_SORT_TYPE: AvailabilityOrder = AvailabilityOrder.Popular;
export const DISCOVERY_ITINERARY_SEARCHES_NUMBER_OF_DAYS = 4;

export interface RecipeStateProps {
  pax?: Pax;
  recipe?: RecipeMainIngredient[];
  preferredDay?: Date;
  preferredMonth?: MonthIndex;
  itinerarySuggestionSortType?: AvailabilityOrder;
  returnTransport?: ConnectionType;
  metadata?: RecipeMetaData;
  searchForPackageRates?: boolean | null;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function setupRecipeState(props: RecipeStateProps) {
  const preferredTimeStore = usePreferredTimeStore();
  preferredTimeStore.setInitialValueForDayAndMonth(
    props.preferredDay,
    props.preferredMonth
  );
  const editingIngredientStore = useEditingIngredientStore();

  const pax = ref<TripPlannerPax>(
    new TripPlannerPax(props.pax ?? [{ numberOfAdults: 2, childAges: [] }])
  );
  function getInitialRecipe(
    ingredients: RecipeMainIngredient[]
  ): RecipeMainIngredient[] {
    return ingredients.map((ingredient, index) => {
      if (
        isStayIngredient(ingredient) &&
        index >= 1 &&
        ingredient.transportTypeToBeReached === ConnectionType.NONE
      ) {
        ingredient.transportTypeToBeReached =
          DEFAULT_TRANSPORT_TYPE_BETWEEN_DESTINATION;
      }

      if (
        !isOwnStayIngredient(ingredient) &&
        props.itinerarySuggestionSortType
      ) {
        ingredient.availabilityOrder = props.itinerarySuggestionSortType;
      }

      return ingredient;
    });
  }
  const recipe = ref<RecipeMainIngredient[]>(
    props.recipe ? getInitialRecipe(props.recipe) : []
  );
  const hasRecipeIngredients = computed<boolean>(() => recipe.value.length > 0);

  const settingTransportIngredient = ref<RecipeMainIngredient | null>(null);

  const returnTransport = ref<ConnectionType | undefined>(
    props.returnTransport
  );

  const metaData = ref<RecipeMetaData | undefined>(props.metadata);

  const searchForPackageRates = ref<boolean | undefined | null>(
    props.searchForPackageRates
  );

  const ownStayOnlyRecipe = computed<OwnStayIngredient[]>(() =>
    recipe.value.filter(isOwnStayIngredient)
  );
  const hotelOnlyRecipe = computed<HotelIngredient[]>(() =>
    recipe.value.filter(isHotelIngredient)
  );
  const stayOnlyRecipe = computed<(HotelIngredient | OwnStayIngredient)[]>(() =>
    recipe.value.filter(isStayIngredient)
  );
  const flightOnlyRecipe = computed<FlightIngredient[]>(() =>
    recipe.value.filter(isFlightIngredient)
  );

  const ferryOnlyRecipe = computed<FerryIngredient[]>(() =>
    recipe.value.filter(isFerryIngredient)
  );

  const activityOnlyRecipe = computed<ActivityIngredient[]>(() =>
    recipe.value.reduce(
      (
        activityIngredients: ActivityIngredient[],
        currentIngredient: RecipeMainIngredient
      ) => {
        if (
          isStayIngredient(currentIngredient) &&
          currentIngredient.subIngredients.length
        ) {
          return [...activityIngredients, ...currentIngredient.subIngredients];
        }
        return activityIngredients;
      },
      []
    )
  );

  const startingTransportExists = computed<boolean>(() => {
    const firstStayIngredient = stayOnlyRecipe.value[0];
    const firstIngredient = recipe.value[0];

    return (
      !!firstStayIngredient &&
      (firstStayIngredient.transportTypeToBeReached !== ConnectionType.NONE ||
        (!!firstIngredient && isTransportIngredient(firstIngredient)))
    );
  });

  const endingTransportExists = computed<boolean>(() => {
    const lastIngredient = last(recipe.value);

    return (
      (stayOnlyRecipe.value[0] &&
        !!endLocationIngredient.value &&
        endLocationIngredient.value.transportTypeToBeReached !==
          ConnectionType.NONE) ||
      (!!lastIngredient && isTransportIngredient(lastIngredient))
    );
  });

  const isSettingFirstTransport = computed<boolean>(() => {
    const firstStayIngredient = stayOnlyRecipe.value[0];
    assertValue(
      firstStayIngredient,
      'recipeState.ts > isSettingFirstTransport = computed > firstStayIngredient'
    );

    return (
      !!settingTransportIngredient.value &&
      settingTransportIngredient.value.ingredientId ===
        firstStayIngredient.ingredientId
    );
  });

  const searchActivities = ref<boolean>(false);

  const transportsByIngredient = computed<Dictionary<ConnectionType>>(() =>
    recipe.value.reduce<Dictionary<ConnectionType>>((acc, component) => {
      acc[component.ingredientId] = component.transportTypeToBeReached;
      return acc;
    }, {})
  );

  const selectedActivityTickets = ref<ActivityTicket[]>([]);

  const isRecipeCorrect = computed(() =>
    recipe.value.every((ingredient: RecipeMainIngredient) =>
      ingredient.isIngredientCorrect()
    )
  );

  const doesAnyStayHaveSubingredients = computed<boolean>(() =>
    stayOnlyRecipe.value.some((ingredient) => ingredient.subIngredients.length)
  );

  const amountOfIngredientsInRecipe = computed<number>(() =>
    recipe.value.reduce(
      (accumulator: number, ingredient: RecipeMainIngredient) => {
        if (isStayIngredient(ingredient)) {
          return accumulator + ingredient.subIngredients.length + 1;
        }
        return accumulator + 1;
      },
      0
    )
  );

  function addIngredient(
    ingredient: RecipeMainIngredient,
    index?: number
  ): void {
    if (isStayIngredient(ingredient)) {
      const lastIngredient = last(recipe.value);
      const hasReturnFlight =
        lastIngredient && isFlightIngredient(lastIngredient);

      if (recipe.value.length) {
        ingredient.transportTypeToBeReached = DEFAULT_TRANSPORT_TYPE;
      }

      recipe.value.push(ingredient);

      if (recipe.value.length === 1) {
        setReturnLocation();
      }

      if (hasReturnFlight && isHotelIngredient(ingredient)) {
        updateSuggestedReturnFlight(ingredient.searchText, lastIngredient);
      }

      return;
    }

    const thereIsIndex = typeof index === 'number' && index >= 0;

    if (thereIsIndex) {
      recipe.value.splice(index, 0, ingredient);
    } else {
      recipe.value.push(ingredient);
    }
  }

  function addCarTripIngredient(ingredient: RecipeMainIngredient): void {
    if (!isStayIngredient(ingredient)) {
      return;
    }

    ingredient.transportTypeToBeReached =
      DEFAULT_TRANSPORT_TYPE_BETWEEN_DESTINATION;

    recipe.value.push(ingredient);

    if (recipe.value.length === 1) {
      setReturnLocation();
    }
  }

  function deleteIngredientById(ingredientId: IngredientId): void {
    const ingredient = getIngredient(ingredientId);
    deleteIngredient(ingredient);
  }

  function updateIngredientById(data: UpdateIngredientByIdData): void {
    switch (data.updateType) {
      case UpdateIngredientDataType.StayIngredientSearchText: {
        const stayIngredient = getStayIngredient(data.ingredientId);
        if (!stayIngredient) {
          return;
        }
        if (isOwnStayIngredient(stayIngredient)) {
          const newHotelIngredient = new HotelIngredient();
          newHotelIngredient.setSearchText(data.searchText);
          newHotelIngredient.numberOfNights = stayIngredient.numberOfNights;
          newHotelIngredient.transportTypeToBeReached =
            stayIngredient.transportTypeToBeReached;

          replaceIngredient(stayIngredient, newHotelIngredient);
        }

        if (isHotelIngredient(stayIngredient)) {
          stayIngredient.setSearchText(data.searchText);
          stayIngredient.heartedHotels = [];
          stayIngredient.subIngredients = [];
          stayIngredient.polygonConstraint = undefined;
          stayIngredient.nearbyPolygonText = undefined;
        }
        break;
      }

      default:
        exhaustiveGuard(
          data.updateType,
          'Unhandled type in updateIgredientById'
        );
    }
  }

  function updateHotelIngredientById(
    ingredientId: IngredientId,
    searchText: string
  ): void {
    return updateIngredientById({
      updateType: UpdateIngredientDataType.StayIngredientSearchText,
      searchText,
      ingredientId,
    });
  }

  function replaceIngredient(
    oldIngredient: RecipeMainIngredient,
    newIngredient: RecipeMainIngredient
  ): void {
    const oldIngredientIndex = getIngredientIndex(oldIngredient.ingredientId);

    recipe.value.splice(oldIngredientIndex, 1, newIngredient);
  }

  function deleteIngredient(ingredient: RecipeIngredient): void {
    const numberOfStayIngredients = stayOnlyRecipe.value.length;

    const shouldClearRecipe =
      isStayIngredient(ingredient) && numberOfStayIngredients === 1;

    if (shouldClearRecipe) {
      recipe.value = [];
      return;
    }

    if (isHotelIngredient(ingredient)) {
      updateConsecutiveFlightIngredients(ingredient);
    }
    if (isTransportIngredient(ingredient) || isStayIngredient(ingredient)) {
      recipe.value.splice(recipe.value.indexOf(ingredient), 1);
    } else {
      deleteSubIngredient(
        ingredient.code,
        ingredient.parentIngredientId,
        ingredient.ingredientId
      );
    }
  }

  function deleteSubIngredient(
    subIngredientCode: string,
    parentIngredientId: IngredientId,
    ingredientId: string
  ): void {
    const parentIngredient = getStayIngredient(parentIngredientId);
    if (!parentIngredient) {
      return;
    }
    parentIngredient.subIngredients = parentIngredient.subIngredients.filter(
      (subIngredient) =>
        !(
          subIngredient.code === subIngredientCode &&
          subIngredient.ingredientId === ingredientId
        )
    );
  }

  function areTransportsMirrored(
    transport1: TransportIngredient,
    transport2: TransportIngredient
  ): boolean {
    return (
      transport1.fromPort === transport2.toPort &&
      transport1.toPort === transport2.fromPort
    );
  }

  //TODO Check how to improve this function or remove it after the flex transport selection.
  //More if than needed to avoid creating a new flight and to pushing it into the correct recipe place.
  function updateConsecutiveFlightIngredients(
    ingredient: HotelIngredient
  ): void {
    const indexOfIngredient = recipe.value.indexOf(ingredient);
    const previousIngredient = recipe.value[indexOfIngredient - 1] ?? null;
    const nextIngredient = recipe.value[indexOfIngredient + 1] ?? null;

    const previousIngredientIsFlgiht =
      previousIngredient && isFlightIngredient(previousIngredient);
    const nextIngredientIsFlgiht =
      nextIngredient && isFlightIngredient(nextIngredient);
    const connectedIngredientsAreFlights =
      previousIngredientIsFlgiht && nextIngredientIsFlgiht;

    if (
      connectedIngredientsAreFlights &&
      !areTransportsMirrored(previousIngredient, nextIngredient)
    ) {
      nextIngredient.fromPort = previousIngredient.fromPort;
      nextIngredient.fromPortDisplayText =
        previousIngredient.fromPortDisplayText;
      nextIngredient.fromPortText = previousIngredient.fromPortText;
      deleteIngredient(previousIngredient);
      return;
    }

    if (previousIngredient && isTransportIngredient(previousIngredient)) {
      deleteIngredient(previousIngredient);
    }

    if (nextIngredient && isFlightIngredient(nextIngredient)) {
      const indexOfHotelIngredient = hotelOnlyRecipe.value.indexOf(ingredient);

      const isLastHotelIngredient =
        indexOfHotelIngredient === hotelOnlyRecipe.value.length - 1;

      if (!isLastHotelIngredient) {
        // TODO: update next flight with suggested instead of removing
        deleteIngredient(nextIngredient);
        return;
      }

      const prevoiusHotelIngredient =
        hotelOnlyRecipe.value[indexOfHotelIngredient - 1];

      if (!prevoiusHotelIngredient) {
        return;
      }

      updateSuggestedReturnFlight(
        prevoiusHotelIngredient.searchText,
        nextIngredient
      );
    }
  }

  function updateSuggestedReturnFlight(
    newFromPortSearchText: string,
    previousReturnFlight: FlightIngredient
  ): void {
    const endpoint = new FlightEndpoint();

    deleteIngredient(previousReturnFlight);

    endpoint
      .getSuggestedAirport(newFromPortSearchText)
      .subscribe((airportStaticInfo) => {
        if (airportStaticInfo) {
          const newReturnFlight = new FlightIngredient();
          newReturnFlight.fromPort = airportStaticInfo.code;
          newReturnFlight.fromPortText = airportStaticInfo.city;
          newReturnFlight.fromPortDisplayText = airportStaticInfo.label;

          newReturnFlight.toPort = previousReturnFlight.toPort;
          newReturnFlight.toPortText = previousReturnFlight.toPortText;
          newReturnFlight.toPortDisplayText =
            previousReturnFlight.toPortDisplayText;

          addIngredient(newReturnFlight);
        } else {
          returnTransport.value = ConnectionType.CAR;
        }
      });
  }

  function getIngredient(id: string): RecipeIngredient {
    const ingredients = recipe.value;
    const subIngredients = stayOnlyRecipe.value.flatMap(
      (i) => i.subIngredients
    );
    const foundIngredient = [...ingredients, ...subIngredients].find(
      (ingredient) => ingredient.ingredientId === id
    );

    if (!foundIngredient) {
      throw new Error(`Ingredient with ID:${id} not found`);
    }

    return foundIngredient;
  }

  function getIngredientIndex(id: string): number {
    return recipe.value.findIndex(
      (ingredient) => ingredient.ingredientId === id
    );
  }

  function getOwnStayIngredient(ingredientId: string): OwnStayIngredient {
    const foundIngredient = ownStayOnlyRecipe.value.find(
      (ownStayIngredient) => ownStayIngredient.ingredientId === ingredientId
    );

    if (!foundIngredient) {
      throw new Error('OwnStayIngredient not found');
    }

    return foundIngredient;
  }

  function getHotelIngredient(
    hotelIngredientId: IngredientId
  ): HotelIngredient {
    const foundIngredient = hotelOnlyRecipe.value.find(
      (hotelIngredient) => hotelIngredient.ingredientId === hotelIngredientId
    );

    if (!foundIngredient) {
      throw new Error('HotelIngredient not found');
    }

    return foundIngredient;
  }

  function getStayIngredient(
    ingredientId: IngredientId
  ): OwnStayIngredient | HotelIngredient | undefined {
    const foundIngredient = recipe.value.find(
      (ingredient) => ingredient.ingredientId === ingredientId
    );

    if (!foundIngredient || !isStayIngredient(foundIngredient)) {
      return;
    }

    return foundIngredient;
  }

  function getFlightIngredient(
    flightIngredientId: IngredientId
  ): FlightIngredient {
    const foundIngredient = flightOnlyRecipe.value.find(
      (flightIngredient) => flightIngredient.ingredientId === flightIngredientId
    );

    if (!foundIngredient) {
      throw new Error('FlightIngredient not found');
    }

    return foundIngredient;
  }

  function getFerryIngredient(
    ferryIngredientId: IngredientId
  ): FerryIngredient {
    const foundIngredient = ferryOnlyRecipe.value.find(
      (ferryIngredient) => ferryIngredient.ingredientId === ferryIngredientId
    );

    if (!foundIngredient) {
      throw new Error('FerryIngredient not found');
    }

    return foundIngredient;
  }

  function getTransportIngredient(
    transportIngredientId: IngredientId
  ): FlightIngredient | FerryIngredient {
    const foundIngredient = recipe.value.find(
      (ingredient) => ingredient.ingredientId === transportIngredientId
    );

    if (!foundIngredient || !isTransportIngredient(foundIngredient)) {
      throw new Error('TransportIngredient not found');
    }

    return foundIngredient;
  }

  function getActivityIngredient(
    activityIngredientId: IngredientId
  ): ActivityIngredient {
    const subIngredients = stayOnlyRecipe.value.flatMap(
      (stayIngredient) => stayIngredient.subIngredients
    );

    const foundIngredient = subIngredients.find(
      (subIngredient) => subIngredient.ingredientId === activityIngredientId
    );

    if (!foundIngredient) {
      throw new Error('ActivityIngredient not found');
    }

    return foundIngredient;
  }

  function getSubIngredientsByCode(
    parentIngredientId: IngredientId,
    subIngredientCode: string
  ): ActivityIngredient[] | undefined {
    const ingredient = getStayIngredient(parentIngredientId);

    if (!ingredient) {
      return;
    }

    const subIngredients = asq(ingredient.subIngredients ?? [])
      .pipe(
        filter(
          (subIngredient: ActivityIngredient) =>
            subIngredient.code === subIngredientCode
        )
      )
      .toArray();

    return subIngredients;
  }

  function createHotelIngredient(): void {
    editingIngredientStore.editingStayIngredient = new HotelIngredient();
    editingIngredientStore.editingStayIngredient.isNewIngredient = true;
  }

  function createFlightIngredient(): void {
    editingIngredientStore.flightIngredientFrom = 'recipe';
    editingIngredientStore.editingFlightIngredient = new FlightIngredient();
    editingIngredientStore.editingFlightIngredient.isNewIngredient = true;
  }

  function createFerryIngredient(): void {
    editingIngredientStore.ferryIngredientFrom = 'recipe';
    editingIngredientStore.editingFerryIngredient = new FerryIngredient();
    editingIngredientStore.editingFerryIngredient.isNewIngredient = true;
  }

  function createTransportIngredient(type: ConnectionType): void {
    if (isFlightTransport(type)) {
      createFlightIngredient();
    } else if (isFerryTransport(type)) {
      createFerryIngredient();
    } else {
      throw Error(`Cannot create new Transport ingredient from type: ${type}`);
    }
  }

  function editIngredientTransport(ingredient: RecipeMainIngredient): void {
    settingTransportIngredient.value = ingredient;
  }

  const searchSuggestionResults = ref<AutoCompleteSuggestionsByCategory[]>([]);

  function setSearchSuggestionResults(
    response: AutoCompleteSuggestionsByCategory[]
  ): void {
    searchSuggestionResults.value = response;
  }

  function updatePortsFromOtherTransport(
    toTransport: TransportIngredient,
    toCheckPoint: TransportDirection,
    fromTransport: TransportIngredient,
    fromCheckPoint: TransportDirection
  ): void {
    if (toCheckPoint === 'from') {
      if (fromCheckPoint === 'from') {
        toTransport.fromPort = fromTransport.fromPort;
        toTransport.fromPortDisplayText = fromTransport.fromPortDisplayText;
        toTransport.fromPortText = fromTransport.fromPortText;
      } else {
        toTransport.fromPort = fromTransport.toPort;
        toTransport.fromPortDisplayText = fromTransport.toPortDisplayText;
        toTransport.fromPortText = fromTransport.toPortText;
      }
    } else {
      if (fromCheckPoint === 'from') {
        toTransport.toPort = fromTransport.fromPort;
        toTransport.toPortDisplayText = fromTransport.fromPortDisplayText;
        toTransport.toPortText = fromTransport.fromPortText;
      } else {
        toTransport.toPort = fromTransport.toPort;
        toTransport.toPortDisplayText = fromTransport.toPortDisplayText;
        toTransport.toPortText = fromTransport.toPortText;
      }
    }
  }

  function getMirroredTransport(
    transport: FlightIngredient | FerryIngredient
  ): FlightIngredient | FerryIngredient {
    const returnTransport = isFlightIngredient(transport)
      ? new FlightIngredient()
      : new FerryIngredient();
    updatePortsFromOtherTransport(returnTransport, 'from', transport, 'to');
    updatePortsFromOtherTransport(returnTransport, 'to', transport, 'from');

    if (isFerryIngredient(transport) && isFerryIngredient(returnTransport)) {
      returnTransport.vehicles = transport.vehicles;
      returnTransport.allowMultipleVehicles = transport.allowMultipleVehicles;
    }

    return returnTransport;
  }

  function setReturnLocation(): void {
    if (!endLocationIngredient.value) {
      endLocationIngredient.value = new HotelIngredient(true);
    }

    const firstIngredient = recipe.value[0];
    assertValue(
      firstIngredient,
      'recipeState.ts > setReturnLocation > firstIngredient'
    );

    endLocationIngredient.value.transportTypeToBeReached =
      returnTransport.value !== undefined
        ? returnTransport.value
        : firstIngredient.transportTypeToBeReached;
  }

  const endLocationIngredient = ref<HotelIngredient>();

  if (recipe.value.length) {
    setReturnLocation();
  }

  function setTransportInSettingTransportIngredient(
    type: ConnectionType
  ): void {
    assertValue(
      settingTransportIngredient.value,
      'recipeState > setTransportInSettingTransportIngredient > settingTransportIngredient'
    );
    settingTransportIngredient.value.transportTypeToBeReached = type;
  }

  function getExistingSelectedTickets(
    activityCode: string,
    ingredientId: IngredientId
  ): ActivityTicket[] {
    const ingredient = getStayIngredient(ingredientId);
    if (!ingredient) {
      return [];
    }
    const activitySubIngredients = ingredient.subIngredients?.filter(
      (subIngredient) => subIngredient.code === activityCode
    );

    if (!activitySubIngredients?.length) {
      return [];
    }

    return activitySubIngredients.flatMap((subIngredient) =>
      subIngredient.tickets.map((selectedTicket) => ({
        ...selectedTicket,
        code: subIngredient.code,
        dateOffset: subIngredient.offset,
      }))
    );
  }

  function getSelectedTicketsForRate(
    rateId: string,
    dateOffset: number
  ): ActivityTicket[] {
    return selectedActivityTickets.value.filter(
      (selectedTicket) =>
        selectedTicket.rate.id === rateId &&
        selectedTicket.dateOffset === dateOffset
    );
  }

  function updateSelectedTickets(
    updatedTickets: ActivityTicket[],
    rateId: string,
    dateOffset: number
  ): void {
    selectedActivityTickets.value = selectedActivityTickets.value.filter(
      (selectedTicket) =>
        !(
          selectedTicket.dateOffset === dateOffset &&
          selectedTicket.rate.id === rateId
        )
    );

    selectedActivityTickets.value =
      selectedActivityTickets.value.concat(updatedTickets);
  }

  function getIngredientDateInRecipe(
    ingredientId: IngredientId,
    selectedDate: Date
  ): Date | undefined {
    const ingredientIndex = getIngredientIndex(ingredientId);

    if (ingredientIndex === -1) {
      throw new Error('Can not find ingredient index.');
    }

    let hasMissingNumberOfNights = false;

    const ingredientOffset = recipe.value.reduce(
      (offset: number, ingredient, index) => {
        if (isStayIngredient(ingredient) && index < ingredientIndex) {
          isDefined(ingredient.numberOfNights)
            ? (offset += ingredient.numberOfNights)
            : (hasMissingNumberOfNights = true);
        }

        return offset;
      },
      0
    );

    if (hasMissingNumberOfNights) {
      return;
    }

    return new SemboDate(selectedDate).add({ days: ingredientOffset }).get();
  }

  function deleteDestinationIngredient(ingredientId: IngredientId): void {
    const ingredient = getIngredient(ingredientId);

    if (ingredient && !ingredient.isNewIngredient) {
      if (isHotelIngredient(ingredient)) {
        trackDestinationRemoved();
        updateConsecutiveFlightIngredients(ingredient);
      }
      deleteIngredient(ingredient);
    }
  }

  function removeAllSelectedRooms(): void {
    hotelOnlyRecipe.value.forEach((hotel) =>
      hotel.heartedHotels.forEach((heartedHotel) => {
        heartedHotel.rooms = [];
        heartedHotel.roomCodes = [];
      })
    );
  }

  const result = {
    pax,
    recipe,
    hasRecipeIngredients,
    ownStayOnlyRecipe,
    hotelOnlyRecipe,
    stayOnlyRecipe,
    flightOnlyRecipe,
    ferryOnlyRecipe,
    metaData,
    searchForPackageRates,
    searchActivities,
    transportsByIngredient,
    selectedActivityTickets,
    startingTransportExists,
    endingTransportExists,
    settingTransportIngredient,
    endLocationIngredient,
    returnTransport,
    isSettingFirstTransport,
    setReturnLocation,
    addIngredient,
    addCarTripIngredient,
    replaceIngredient,
    deleteIngredient,
    deleteIngredientById,
    getIngredient,
    getIngredientIndex,
    getOwnStayIngredient,
    getHotelIngredient,
    getStayIngredient,
    getFlightIngredient,
    getFerryIngredient,
    getTransportIngredient,
    getActivityIngredient,
    getSubIngredientsByCode,
    createHotelIngredient,
    setSearchSuggestionResults,
    createTransportIngredient,
    editIngredientTransport,
    setTransportInSettingTransportIngredient,
    getExistingSelectedTickets,
    getSelectedTicketsForRate,
    updateSelectedTickets,
    updatePortsFromOtherTransport,
    getMirroredTransport,
    updateConsecutiveFlightIngredients,
    isRecipeCorrect,
    getIngredientDateInRecipe,
    doesAnyStayHaveSubingredients,
    deleteDestinationIngredient,
    updateHotelIngredientById,
    amountOfIngredientsInRecipe,
    removeAllSelectedRooms,
    activityOnlyRecipe,
  };

  return result;
}

export type RecipeState = ReturnType<typeof setupRecipeState>;
